
import React, { useState } from 'react';
import { format } from 'date-fns';
import { RefreshCw, Search, Copy } from 'lucide-react';
import { Signal } from '@/types/signal';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { TablePagination } from '@/components/ui/table-pagination';
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';

interface BotSignalsTableProps {
  signals: Signal[];
  isLoading: boolean;
  onRefresh: () => void;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const BotSignalsTable = ({ 
  signals, 
  isLoading, 
  onRefresh,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange
}: BotSignalsTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation();
  const getStatusStyle = (status: string) => {
    switch (status.toLowerCase()) {
      case 'processed':
      case 'success':
        return 'bg-green-500/20 text-green-500 border-green-500/30';
      case 'failed':
        return 'bg-red-500/20 text-red-500 border-red-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    toast.success(type + " " + t('copied_to_clipboard'));
  };

  const filteredSignals = signals.filter((signal) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      signal.id.toLowerCase().includes(query) ||
      signal.instrument.toLowerCase().includes(query)
    );
  });

  return (
    <div>
      <div className="flex justify-between mb-4 gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder={t("search_by_signal_id_or_instrument")}
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onRefresh}
          className="flex items-center gap-1"
        >
          <RefreshCw size={14} />
          {t("refresh")}
        </Button>
      </div>
      
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : filteredSignals.length > 0 ? (
        <>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("date", "Date")}</TableHead>
                  <TableHead>{t("signal_id", "Signal ID")}</TableHead>
                  <TableHead>{t("action", "Action")}</TableHead>
                  <TableHead>{t("instrument", "Instrument")}</TableHead>
                  <TableHead>{t("amount", "Amount")}</TableHead>
                  <TableHead>{t("status", "Status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSignals.map((signal) => (
                  <TableRow key={signal.id}>
                    <TableCell className="font-medium">
                      {format(new Date(signal.created_at), 'MMM d, HH:mm:ss')}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-1 font-mono"
                        onClick={() => copyToClipboard(signal.id, t('signal_id', 'Signal ID'))}
                      >
                        {signal.id.substring(0, 8)}...
                        <Copy size={12} className="ml-1" />
                      </Button>
                    </TableCell>
                    <TableCell className="uppercase font-medium">
                      {signal.action}
                    </TableCell>
                    <TableCell>{signal.instrument}</TableCell>
                    <TableCell>{signal.amount}</TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={getStatusStyle(signal.status)}
                      >
                        {signal.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          <TablePagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p>{t('no_signals_received')}</p>
          <p className="text-sm mt-1">
            {t('signals_will_appear')}
          </p>
        </div>
      )}
    </div>
  );
};
